// src/services/DataSourceService.js
// Manages external data source registrations per entity.
// FAS C foundation — unblocks INFRA-061 (postgres adapter), INFRA-062 (mssql adapter),
// INFRA-063 (REST adapter), and ADM-101 (Data Sources UI).
//
// Storage key per entity: rms_data_sources_${entityId}
// Schema per data source:
//   { id, name, type, config, createdAt, updatedAt,
//     lastTestedAt, lastTestStatus, lastTestError }

import storage from './StorageAdapter';
import auditService from './AuditService';
import entityService from './EntityService';
import { createClient } from '@supabase/supabase-js';
import { applyDualWriteMixin, makeDualWriteMixin } from './_dualWriteHelpers';
import dataSourcesApi from '../api/dataSources';

const STORAGE_KEY_PREFIX = 'rms_data_sources_';
const STATE_KEY = 'sources';

export const VALID_TYPES = ['supabase', 'postgres', 'mssql', 'rest', 'csv', 'mock'];

// Credential field names that must never appear in audit logs
const CREDENTIAL_FIELDS = new Set([
  'password', 'apiKey', 'api_key', 'token', 'secret',
  'secretKey', 'secret_key', 'apiSecret', 'api_secret',
  'accessToken', 'access_token', 'serviceKey', 'service_key',
  'anonKey', 'anon_key', 'serviceRoleKey', 'service_role_key',
]);

/**
 * Returns a shallow copy of config with credential fields replaced by '[REDACTED]'.
 * Exported for unit testing.
 */
export function redactCredentials(config = {}) {
  const out = {};
  for (const [k, v] of Object.entries(config)) {
    out[k] = CREDENTIAL_FIELDS.has(k) ? '[REDACTED]' : v;
  }
  return out;
}

// --- Per-type connection ping functions ---
// Each returns { success: boolean, latencyMs: number, error?: string }

async function pingMock(_config) {
  return { success: true, latencyMs: 0 };
}

async function pingSupabase(config) {
  const start = Date.now();
  try {
    const client = createClient(
      config.url || '',
      config.anonKey || config.anon_key || config.serviceKey || config.service_key || ''
    );
    const { error } = await client.from('rms_config').select('key').limit(1);
    if (error) throw new Error(error.message);
    return { success: true, latencyMs: Date.now() - start };
  } catch (err) {
    return { success: false, latencyMs: Date.now() - start, error: err.message };
  }
}

async function pingRest(config) {
  const start = Date.now();
  const url = config.baseUrl || config.url || '';
  if (!url) return { success: false, latencyMs: 0, error: 'No URL configured' };
  try {
    await fetch(url, { method: 'HEAD' });
    return { success: true, latencyMs: Date.now() - start };
  } catch (err) {
    return { success: false, latencyMs: Date.now() - start, error: err.message };
  }
}

function pingNotImplemented(typeName) {
  return async (_config) => ({
    success: false,
    latencyMs: 0,
    error: `${typeName} adapter not yet implemented`,
  });
}

// Registry maps data source type → ping function.
// Exported (as mutable reference) so tests can inject stubs without jest.mock().
export const adapterPing = {
  mock:     pingMock,
  supabase: pingSupabase,
  rest:     pingRest,
  postgres: pingNotImplemented('PostgreSQL'),
  mssql:    pingNotImplemented('MSSQL'),
  csv:      pingNotImplemented('CSV'),
};

// ---------------------------------------------------------------------------

class DataSourceService {
  constructor() {
    this.listeners = [];
  }

  // --- Storage helpers ---

  _entityKey(_baseKey) {
    const entityId = entityService.getActiveEntityId();
    return entityId ? `${STORAGE_KEY_PREFIX}${entityId}` : `${STORAGE_KEY_PREFIX}default`;
  }

  _storageKey() {
    return this._entityKey(STATE_KEY);
  }

  _load() {
    return storage.getJSON(this._storageKey(), []);
  }

  async _save(data) {
    await this._dualWriteJson(STATE_KEY, data);
    this._notify();
  }

  notifyListeners() { this._notify(); }

  async initFromBackend() {
    return this._initFromBackend([STATE_KEY], []);
  }

  _generateId() {
    return 'ds-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
  }

  // --- Public CRUD API ---

  /**
   * Returns all data sources for the active entity.
   * @returns {Promise<Array>}
   */
  async listDataSources() {
    return this._load();
  }

  /**
   * Returns a single data source by ID, or null if not found.
   * @param {string} id
   * @returns {Promise<object|null>}
   */
  async getDataSource(id) {
    const list = this._load();
    return list.find(ds => ds.id === id) || null;
  }

  /**
   * Creates a new data source.
   * @param {object} data  { name, type, config? }
   * @returns {Promise<object>} created data source
   */
  async createDataSource(data) {
    if (!data || !data.name) throw new Error('DataSource name is required');
    if (!VALID_TYPES.includes(data.type)) throw new Error(`Invalid type: ${data.type}`);

    const list = this._load();
    const now = new Date().toISOString();
    const ds = {
      id:              this._generateId(),
      name:            data.name,
      type:            data.type,
      config:          data.config || {},
      createdAt:       now,
      updatedAt:       now,
      lastTestedAt:    null,
      lastTestStatus:  null,
      lastTestError:   null,
    };
    list.push(ds);
    await this._save(list);

    auditService.logCreate('DataSources', 'DataSource', ds.id, {
      name: ds.name,
      type: ds.type,
      config: redactCredentials(ds.config),
    });

    return ds;
  }

  /**
   * Updates an existing data source.
   * Returns the updated record, or null if not found.
   * @param {string} id
   * @param {object} data  Partial updates (name, type, config, …)
   * @returns {Promise<object|null>}
   */
  async updateDataSource(id, data) {
    const list = this._load();
    const idx = list.findIndex(ds => ds.id === id);
    if (idx === -1) return null;

    const updated = {
      ...list[idx],
      ...data,
      id,                               // prevent id overwrite
      updatedAt: new Date().toISOString(),
    };
    list[idx] = updated;
    await this._save(list);

    auditService.logUpdate('DataSources', 'DataSource', id, {
      name:   updated.name,
      type:   updated.type,
      config: redactCredentials(updated.config || {}),
    });

    return updated;
  }

  /**
   * Deletes a data source by ID.
   * @param {string} id
   * @returns {Promise<boolean>}
   */
  async deleteDataSource(id) {
    const list = this._load();
    const ds = list.find(d => d.id === id);
    if (!ds) return false;

    const filtered = list.filter(d => d.id !== id);
    await this._save(filtered);

    auditService.logDelete('DataSources', 'DataSource', id);

    return true;
  }

  /**
   * Tests connectivity of a data source by calling its adapter's ping() function.
   * Updates lastTestedAt / lastTestStatus / lastTestError on the stored record.
   * @param {string} id
   * @returns {Promise<{ success: boolean, latencyMs: number, error?: string, testedAt: string }>}
   */
  async testDataSource(id) {
    const ds = await this.getDataSource(id);
    if (!ds) return { success: false, latencyMs: 0, error: 'DataSource not found' };

    const pingFn = adapterPing[ds.type] || (async () => ({
      success:   false,
      latencyMs: 0,
      error:     `No adapter available for type: ${ds.type}`,
    }));

    const result = await pingFn(ds.config || {});
    const now = new Date().toISOString();

    // Persist test result — update the stored record in-place
    const list = this._load();
    const idx = list.findIndex(d => d.id === id);
    if (idx !== -1) {
      list[idx] = {
        ...list[idx],
        lastTestedAt:   now,
        lastTestStatus: result.success ? 'ok' : 'error',
        lastTestError:  result.success ? null : (result.error || 'Unknown error'),
      };
      await this._save(list);
    }

    auditService.log('TEST_DATA_SOURCE', 'DataSources', {
      dataSourceId:   id,
      dataSourceName: ds.name,
      type:           ds.type,
      success:        result.success,
      latencyMs:      result.latencyMs,
      error:          result.error || null,
    }, result.success ? 'INFO' : 'WARNING');

    return { ...result, testedAt: now };
  }

  // --- Subscriptions ---

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  _notify() {
    this.listeners.forEach(fn => fn());
  }
}

applyDualWriteMixin(DataSourceService, makeDualWriteMixin({
  storage,
  api: dataSourcesApi,
  serviceName: 'DataSourceService',
}));

const dataSourceService = new DataSourceService();
export default dataSourceService;
